import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, forkJoin, Subject, of, concatMap, takeUntil, finalize, map, take } from 'rxjs';
import { format } from 'date-fns';
import { TaskItem } from '../models/task.model';
import {
  AllocationBatchContext,
  AllocationClientPref,
  AllocationEventLogBatch,
  AllocationEventLogCapture,
  AllocationEventLogItem
} from '../models/allocation-event-log.model';
import { EventService } from './event.service';
import { FilterService } from './filter.service';
import { NotificationService } from './notification.service';
import { SchedulerEventsService } from './scheduler-events.service';
import { ConflictManagementService } from './conflict-management.service';
import { PublishProgressService } from './publish-progress.service';
import { EventProcessingService } from './event-processing.service';
import { PermissionsService } from './permissions.service';
import { isSyntheticMissingService } from '../models/task.model';

export type PublishStatus = 'idle' | 'success' | 'partial' | 'failed' | 'canceled';

export interface PublishOutcome {
  status: PublishStatus;
  total: number;
  succeeded: number;
  failed: number;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChangeManagementService {
  private _pendingChangesCount = new BehaviorSubject<number>(0);
  private _conflictsCount = new BehaviorSubject<number>(0);
  private _simultaneousStartsCount = new BehaviorSubject<number>(0);
  private _publishComplete = new BehaviorSubject<boolean>(false);
  private _publishOutcome = new BehaviorSubject<PublishOutcome>({
    status: 'idle',
    total: 0,
    succeeded: 0,
    failed: 0,
    message: ''
  });
  private cancelPublish = new Subject<void>();

  constructor(
    private eventService: EventService,
    private filterService: FilterService,
    private notificationService: NotificationService,
    private schedulerEventsService: SchedulerEventsService,
    private conflictService: ConflictManagementService,
    private progressService: PublishProgressService,
    private eventProcessingService: EventProcessingService,
    private permissionsService: PermissionsService
  ) { }

  get pendingChangesCount$(): Observable<number> {
    return this._pendingChangesCount.asObservable();
  }

  get conflictsCount$(): Observable<number> {
    return this._conflictsCount.asObservable();
  }

  get simultaneousStartsCount$(): Observable<number> {
    return this._simultaneousStartsCount.asObservable();
  }

  get publishComplete$(): Observable<boolean> {
    return this._publishComplete.asObservable();
  }

  get publishOutcome$(): Observable<PublishOutcome> {
    return this._publishOutcome.asObservable();
  }

  updatePendingChangesCount(events: TaskItem[]): void {
    const pendingEvents = events.filter(event => event.pending_change && !isSyntheticMissingService(event));
    const renderedCarerIds = this.filterService.getRenderedCarerIds();
    const visiblePendingEvents = pendingEvents.filter(event =>
      this.filterService.isTaskVisible(event, renderedCarerIds)
    );
    this._pendingChangesCount.next(visiblePendingEvents.length);
    this.updateConflictsCount(events);
  }

  updateConflictsCount(events: TaskItem[]): void {
    const conflictEvents = events.filter(event => event.pending_change_conflicts_with && !isSyntheticMissingService(event));
    const renderedCarerIds = this.filterService.getRenderedCarerIds();
    const visibleConflictEvents = conflictEvents.filter(event =>
      this.filterService.isTaskVisible(event, renderedCarerIds)
    );
    this._conflictsCount.next(visibleConflictEvents.length);
  }

  /**
   * Updates the count of events with simultaneous starts (same carer, same start time)
   * This is different from conflicts in the change management system
   */
  updateSimultaneousStartsCount(count: number): void {
    this._simultaneousStartsCount.next(count);
  }

  cancelPublishing(): void {
    this.cancelPublish.next();
    this._publishComplete.next(false);
  }

  public unallocateConflicts(pendingEvents: TaskItem[]): Observable<string[]> {
    const publishablePendingEvents = pendingEvents.filter(event => !isSyntheticMissingService(event));

    // Create a set of pending event IDs for quick lookup
    const pendingEventIds = new Set(publishablePendingEvents.map(event => event.id));
    
    // Find all conflicts using the hasConflict method
    const conflictingEvents: TaskItem[] = [];
    
    // Check for conflicts that would leave services unallocated
    for (const event of publishablePendingEvents) {
      const conflictId = this.schedulerEventsService.hasConflict(event.id);
      
      if (conflictId) {
        // Check if the conflicting event is also part of pending changes
        if (!pendingEventIds.has(conflictId)) {
          // If the conflicting event is not part of pending changes, we need to alert the user
          // and halt the publish process to avoid leaving services unallocated
          return this.progressService.openErrorDialog(
            `Event ${conflictId} would be left unallocated. Please include it in your changes.`
          );
        }
        
        conflictingEvents.push(event);
      }
    }

    if (conflictingEvents.length === 0) {
      return of([]);
    }

    // Create an array of unallocation observables
    const unallocationObservables = conflictingEvents.map(event =>
      this.eventService.unallocateServiceInstance(event.id)
    );

    // Return an observable that completes when all unallocations are done and returns the unallocated event IDs
    return forkJoin(unallocationObservables).pipe(
      map(() => conflictingEvents.map(event => event.id))
    );
  }

  // Best-effort capture of the published change set for the ML action log.
  // Sends one batch to carehub-api with the before/after state the client
  // already holds, plus lightweight "snapshot" records of the unchanged
  // services so the log carries the day's full final state (see
  // buildLogEvent). Any failure is swallowed so publish is never affected.
  private logPublishBatch(pendingEvents: TaskItem[], unchangedEvents: TaskItem[] = []): void {
    if (!pendingEvents.length) {
      return;
    }
    try {
      // Per-event isolation: one exotic row (scheduler-mutated types, odd
      // synthetic events) must drop that event only, never the whole batch.
      const events: AllocationEventLogItem[] = [];
      for (const ev of pendingEvents) {
        try { events.push(this.buildLogEvent(ev, false)); } catch { /* skip event */ }
      }
      const actionCount = events.length;
      for (const ev of unchangedEvents) {
        try { events.push(this.buildLogEvent(ev, true)); } catch { /* skip event */ }
      }
      if (!events.length) {
        return;
      }

      const batch: AllocationEventLogBatch = { batch_id: this.newBatchId(), events };
      // The batch context (capture v2) is optional: if building it fails the
      // batch still goes, just without one.
      try {
        batch.context = this.buildBatchContext(
          pendingEvents, unchangedEvents, actionCount, events.length - actionCount
        );
      } catch { /* send the batch without a context */ }
      this.eventService.logAllocationBatch(batch).subscribe({
        error: () => { /* best-effort: ignore action-log failures */ },
      });
    } catch {
      /* best-effort: logging must never affect publish */
    }
  }

  // One action-log event. snapshot=true marks a state record (an unchanged
  // service at publish time) rather than a scheduler action: before == after
  // by construction and consumers must exclude it from behaviour features.
  private buildLogEvent(ev: TaskItem, snapshot: boolean): AllocationEventLogItem {
    const before = this.filterService.classifyCarerKind(ev.original_carer_string);
    const after = this.filterService.classifyCarerKind(ev.carer_string);
    return {
      care_activity_instance_id: ev.id,
      cc_quote_detail_id: ev.CCQuoteDetailID ?? null,
      client_id: ev.residentID ?? null,
      // Final/edited instant, tz-aware; falls back to the original start so
      // keystone never receives a null (service_start is required there).
      service_start: this.toBrisbaneIso(ev.start_date ?? ev.original_start_date),
      service_end: this.toBrisbaneIso(ev.end_date),
      original_start: this.toBrisbaneIso(ev.original_start_date),
      original_end: this.toBrisbaneIso(ev.original_end_date),
      before_kind: before.kind,
      before_target_id: before.targetId,
      before_target_name: before.targetName,
      after_kind: after.kind,
      after_target_id: after.targetId,
      after_target_name: after.targetName,
      // `|| null` (not ??): eCase emits "" when a service has no key-staff
      // default, and an empty string server-side would read as a phantom
      // "recommendation overridden".
      recommended_target_id: ev.suggested_carer_string || null,
      recommendation_source: ev.suggested_carer_string ? 'default_carer' : null,
      is_hpac_client: ev.is_hpac ?? null,
      changed_allocation: !snapshot && !!ev.pending_allocation_change,
      changed_timing: !snapshot && !!ev.pending_timing_change,
      had_conflict: !snapshot && ev.pending_change_conflicts_with != null,
      raw: {
        section_label: ev.section_label ?? null,
        is_pm_service: ev.is_pm_service ?? null,
        is_2pa: ev.is_2pa ?? null,
        staff_min: ev.staff_min ?? null,
        staff_max: ev.staff_max ?? null,
        partially_allocated: ev.partially_allocated ?? null,
        // Full id lists: 2PA allocations comma-join two carers and the
        // *_target_id fields only classify the first.
        carer_ids_before: this.splitCarerIds(ev.original_carer_string),
        carer_ids_after: this.splitCarerIds(ev.carer_string),
        activity_id: ev.activity_id ?? null,
        activity_name: ev.activity_name ?? null,
        ad_hoc: ev.ad_hoc ?? null,
        is_staff_event: ev.is_staff_event ?? null,
        is_break: ev.is_break ?? null,
        suggested_carer_name: ev.suggested_carer_name || null,
        // Carer display name as loaded from eCase — never updated on move, so
        // it reflects the ORIGINAL allocation (the after state is in the
        // *_target_name fields).
        original_carer_name: ev.carer_name || null,
        // Capture v2 (keys and meaning: allocation-event-log.model.ts).
        capture_v: 2,
        ...this.buildClientPref(ev),
        ...(snapshot ? { snapshot: true } : this.buildActionCapture(ev)),
      },
    };
  }

  // The client's carer / gender preferences as loaded from eCase; nothing when
  // there are none (staff events, most clients). Ids only: the carer names
  // eCase sends beside them never reach the TaskItem.
  private buildClientPref(ev: TaskItem): { client_pref?: AllocationClientPref } {
    const ids = (list: string[] | null | undefined): string[] =>
      Array.isArray(list) ? list.map(id => String(id ?? '').trim()).filter(id => id.length > 0) : [];
    const preferred = ids(ev.preferred_carers);
    const excluded = ids(ev.excluded_carers);
    const preferredGender = ev.client_preferred_gender || null;
    const excludedGender = ev.client_excluded_gender || null;
    if (!preferred.length && !excluded.length && !preferredGender && !excludedGender) {
      return {};
    }
    return {
      client_pref: {
        preferred_carer_ids: preferred,
        excluded_carer_ids: excluded,
        preferred_gender: preferredGender,
        excluded_gender: excludedGender,
      },
    };
  }

  // Provenance of an action row: what last set the carer / time now pending
  // (stamped by the interaction paths, see allocation-capture.ts) and where
  // that change sits in the session. An origin is reported only for a
  // dimension that IS pending; a pending one with no recorded origin is
  // 'unknown', never a guess.
  private buildActionCapture(ev: TaskItem): AllocationEventLogCapture {
    const allocationOrigin = ev.pending_allocation_change ? (ev.log_allocation_origin ?? 'unknown') : null;
    const bulk = allocationOrigin === 'bulk_row' ? ev.log_bulk : undefined;
    return {
      allocation_origin: allocationOrigin,
      timing_origin: ev.pending_timing_change ? (ev.log_timing_origin ?? 'unknown') : null,
      change_seq: ev.log_change_seq ?? null,
      changed_at: ev.log_changed_at ?? null,
      ...(bulk ? {
        bulk_id: bulk.id,
        bulk_size: bulk.size,
        bulk_source_id: bulk.source_id,
        bulk_target_id: bulk.target_id,
      } : {}),
    };
  }

  // Scope of this publish (capture v2): what the scheduler had loaded and
  // filtered, and why loaded events are in neither list. Publish works from
  // the filtered events; the counts here cover ALL events loaded for the day
  // (the scheduler's own store). Reasons and their order are documented on
  // AllocationBatchContext.
  private buildBatchContext(
    pendingEvents: TaskItem[], unchangedEvents: TaskItem[], actions: number, snapshots: number
  ): AllocationBatchContext {
    const state = this.filterService.getCurrentFilterState();
    const loadedEvents: TaskItem[] = this.schedulerEventsService.getAllEvents();
    const listedIds = new Set<string>();
    for (const ev of pendingEvents) { listedIds.add(String(ev.id)); }
    for (const ev of unchangedEvents) { listedIds.add(String(ev.id)); }

    const now = new Date();
    const loaded = { total: 0, client_services: 0, staff_events: 0, unallocated: 0, on_shift: 0, on_individual: 0 };
    const notLogged = { past: 0, hidden_by_filter: 0, staff_events_unchanged: 0, pending_not_publishable: 0, synthetic: 0 };
    let loadedAt: string | null = null;
    let viewDate: string | null = null;

    for (const ev of loadedEvents) {
      loaded.total++;
      if (ev.is_staff_event) {
        loaded.staff_events++;
      } else {
        loaded.client_services++;
      }
      // String() coercion: carer_string is a NUMBER after a drag.
      const kind = this.filterService.classifyCarerKind(String(ev.carer_string ?? '')).kind;
      if (kind === 'unallocated') {
        loaded.unallocated++;
      } else if (kind === 'shift') {
        loaded.on_shift++;
      } else {
        loaded.on_individual++;
      }

      // Every item of a load carries the same stamp; keep the latest in case.
      if (ev.loaded_at && (!loadedAt || ev.loaded_at > loadedAt)) {
        loadedAt = ev.loaded_at;
        viewDate = ev.loaded_date ?? null;
      }

      if (listedIds.has(String(ev.id))) {
        continue;
      }
      if (isSyntheticMissingService(ev)) {
        notLogged.synthetic++;
      } else if (ev.pending_change) {
        notLogged.pending_not_publishable++;
      } else if (!(new Date(ev.start_date) >= now)) {
        // the lists' own "start >= now" test, failed
        notLogged.past++;
      } else if (ev.is_staff_event) {
        notLogged.staff_events_unchanged++;
      } else {
        // unchanged, still to come, a client service: only visibility is left
        notLogged.hidden_by_filter++;
      }
    }

    const sectionVisibility: { [key: string]: boolean } = state.sectionVisibility ?? {};
    return {
      capture_v: 2,
      view_date: viewDate,
      loaded_at: loadedAt,
      published_at: now.toISOString(),
      auto_allocate_enabled: !!state.autoAllocateEnabled,
      include_missing_services: state.includeMissingServices === true,
      role_tier: this.currentRoleTier(),
      filters: {
        time_of_day: state.timeOfDay ?? null,
        client_type: state.clientType ?? null,
        user_type: state.userType ?? null,
        show_shifts: !!state.showShifts,
        show_staff: !!state.showStaff,
        // Only WHETHER a client-name search is active. Never the text: it is
        // a client's name.
        client_name_filter_active: String(state.clientNameFilter ?? '').trim() !== '',
        hidden_section_keys: Object.keys(sectionVisibility)
          .filter(key => sectionVisibility[key] === false)
          .map(key => Number(key))
          .filter(key => !isNaN(key))
          .sort((a, b) => a - b),
      },
      loaded,
      batch: { actions, snapshots },
      not_logged: notLogged,
    };
  }

  // The signed-in user's highest feature tier. The session is held in a
  // BehaviorSubject, so this resolves synchronously; anything else (no
  // session, an error) leaves null rather than costing the batch its context.
  private currentRoleTier(): 'roster' | 'reallocate' | null {
    let tier: 'roster' | 'reallocate' | null = null;
    try {
      this.permissionsService.getHighestFeatureAccess().pipe(take(1)).subscribe({
        next: value => { tier = value; },
        error: () => { /* leave null */ },
      });
    } catch { /* leave null */ }
    return tier;
  }

  // String() coercion matters: the dhtmlx scheduler mutates carer_string to a
  // NUMBER (the y-axis section key) after drag-and-drop, and .split on a
  // number is the TypeError that silently killed whole batches.
  private splitCarerIds(carerString: string | number | null | undefined): string[] {
    return String(carerString ?? '')
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0);
  }

  // eCase emits naive Brisbane wall-clock strings ("YYYY-MM-DD HH:MM:SS").
  // Assert the +10:00 offset explicitly rather than let the browser's timezone
  // reinterpret them (toISOString/xxx would). Queensland has no DST, so +10:00
  // is constant. An unparseable value is passed through so keystone rejects it
  // visibly (422 -> quarantine) rather than being silently dropped.
  private toBrisbaneIso(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      return value;
    }
    return format(d, "yyyy-MM-dd'T'HH:mm:ss") + '+10:00';
  }

  private newBatchId(): string {
    const c: any = (typeof crypto !== 'undefined') ? crypto : null;
    if (c?.randomUUID) {
      return c.randomUUID();
    }
    // batch_id is the analysis-time dedup key and keystone 422s a non-uuid, so
    // the fallback must still be a valid uuid-v4.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
      const r = (Math.random() * 16) | 0;
      const v = ch === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  getPublishablePendingEvents(events: TaskItem[], renderedCarerIds: Set<number>): TaskItem[] {
    return events.filter(event =>
      event.pending_change &&
      new Date(event.start_date) >= new Date() &&
      !isSyntheticMissingService(event) &&
      this.filterService.isTaskVisible(event, renderedCarerIds)
    );
  }

  // Unchanged-but-eligible client services at publish time. Logged as
  // raw.snapshot=true records so the ML training set sees the day's full final
  // state — otherwise services pre-allocated and never touched are invisible
  // and the log over-represents the "hard" ones. Staff events are excluded
  // (no allocation label worth learning from an untouched break).
  private getSnapshotEvents(events: TaskItem[], renderedCarerIds: Set<number>): TaskItem[] {
    return events.filter(event =>
      !event.pending_change &&
      !event.is_staff_event &&
      new Date(event.start_date) >= new Date() &&
      !isSyntheticMissingService(event) &&
      this.filterService.isTaskVisible(event, renderedCarerIds)
    );
  }

  publishPendingChanges(events: TaskItem[], updateEvent: (eventId: string) => void, loadTasks?: (date: string, restoreScrollPosition: boolean) => void): void {
    // Reset publish complete state at the beginning of a new publish operation
    this._publishComplete.next(false);
    this._publishOutcome.next({
      status: 'idle',
      total: 0,
      succeeded: 0,
      failed: 0,
      message: ''
    });

    let wasCanceled = false;
    
    if (this.cancelPublish.closed) {
      this.cancelPublish = new Subject<void>();
    }

    const renderedCarerIds = this.filterService.getRenderedCarerIds();
    const pendingEvents = this.getPublishablePendingEvents(events, renderedCarerIds);
    const totalPending = pendingEvents.length;
    this._pendingChangesCount.next(totalPending);

    // Capture the staff's intended changes for the ML action log, plus
    // snapshots of the unchanged services (full final-state coverage). Purely
    // additive and fire-and-forget — runs alongside (never gates) publish.
    this.logPublishBatch(pendingEvents, this.getSnapshotEvents(events, renderedCarerIds));

    // Identify conflict groups using ConflictManagementService
    const conflictData = this.conflictService.createConflictGroups(
      pendingEvents,
      (eventId) => this.schedulerEventsService.hasConflict(eventId)
    );
    
    const { nonConflictEvents, conflictGroupEvents, eventToGroupMap, dynamicEventIds } = conflictData;

    // Open the progress dialog
    const { progressDialog, dialogRef } = this.progressService.openProgressDialog(
      nonConflictEvents.length,
      conflictGroupEvents.reduce((count, group) => count + group.events.length, 0)
    );
    
    // Handle dialog close/cancel
    dialogRef.afterClosed().subscribe((result: { canceled?: boolean } | undefined) => {
      if (result?.canceled) {
        wasCanceled = true;
        this.cancelPublish.next();
      }
    });

    // Add steps to the progress dialog
    // First add non-conflict events
    nonConflictEvents.forEach(event => {
      this.progressService.addEventToProgressDialog(event, progressDialog, false);
    });

    // Then add conflict group events
    conflictGroupEvents.forEach(group => {
      group.events.forEach(event => {
        this.progressService.addEventToProgressDialog(event, progressDialog, true, group.groupId);
      });
    });

    // Create an observable chain for processing events
    let publishChain = of(null as any);

    // 1. Process non-conflict events first
    if (nonConflictEvents.length > 0) {
      publishChain = publishChain.pipe(
        concatMap(() => {
          return new Observable((observer: { next: (val: any) => void, error: (err: any) => void, complete: () => void }) => {
            this.eventProcessingService.processEvents(
                nonConflictEvents, 
                updateEvent, 
                progressDialog, 
                eventToGroupMap,
                dynamicEventIds,
                this.cancelPublish
              ).pipe(
                takeUntil(this.cancelPublish),
                finalize(() => {
                  observer.next(null);
                  observer.complete();
                })
              )
              .subscribe({
                error: (err: any) => observer.error(err)
              });
          });
        })
      );
    }

    // 2. Process each conflict group sequentially
    // Sort the conflict groups to ensure consistent processing order
    // Dynamic swap group (which starts with 'z-') should be processed last
    const sortedGroups = [...conflictGroupEvents].sort((a, b) => {
      // Put the dynamic group last
      if (a.groupId.startsWith('z-')) return 1;
      if (b.groupId.startsWith('z-')) return -1;
      
      // Otherwise sort other groups normally
      return a.groupId.localeCompare(b.groupId);
    });
    
    // Process groups in the sorted order
    for (const group of sortedGroups) {
      publishChain = publishChain.pipe(
        concatMap(() => this.eventProcessingService.processConflictGroup(
          group,
          updateEvent,
          progressDialog,
          eventToGroupMap,
          dynamicEventIds,
          this.cancelPublish
        ))
      );
    }
    
    // 3. Process any dynamic conflict group that was created during standard event processing
    // This step is still needed because dynamicEventIds might be populated during runtime 
    // by events that weren't originally part of any conflict group
    publishChain = publishChain.pipe(
      concatMap(() => {
        return new Observable((observer: { next: (val: any) => void, error: (err: any) => void, complete: () => void }) => {
          // Check if any events were added to the dynamic group during processing
          const dynamicGroup = this.conflictService.createDynamicConflictGroup(events, dynamicEventIds);
          
          if (dynamicGroup && dynamicGroup.events.length > 0) {
            this.eventProcessingService.processConflictGroup(
              dynamicGroup,
              updateEvent,
              progressDialog,
              eventToGroupMap,
              dynamicEventIds,
              this.cancelPublish
            ).pipe(
              takeUntil(this.cancelPublish),
              finalize(() => {
                observer.next(null);
                observer.complete();
              })
            ).subscribe({
              error: (err: any) => observer.error(err)
            });
          } else {
            // No dynamic group to process or it's already been processed
            observer.next(null);
            observer.complete();
          }
        });
      })
    );

    // Subscribe to the chain to start the publishing process
    publishChain.pipe(
      takeUntil(this.cancelPublish),
      finalize(() => {
        const completedSteps = progressDialog.steps.filter(step =>
          step.completed
        );
        const failedSteps = completedSteps.filter(step =>
          step.completed && !step.success
        );
        const succeededCount = completedSteps.length - failedSteps.length;
        const failedCount = failedSteps.length;
        const totalSteps = progressDialog.steps.length;
        const hasAnySuccess = succeededCount > 0;
        const hasAnyFailure = failedCount > 0;

        let status: PublishStatus = 'success';
        let message = 'All changes published successfully.';

        if (wasCanceled) {
          status = 'canceled';
          message = hasAnySuccess
            ? `Publishing canceled. ${succeededCount} ${succeededCount === 1 ? 'change was' : 'changes were'} published before cancellation.`
            : 'Publishing canceled. No changes were published.';
        } else if (!hasAnySuccess && (hasAnyFailure || totalSteps > 0)) {
          status = 'failed';
          message = 'No changes were published successfully.';
        } else if (hasAnySuccess && hasAnyFailure) {
          status = 'partial';
          message = `${succeededCount} of ${totalSteps} changes were published. ${failedCount} failed.`;
        }

        this._publishOutcome.next({
          status,
          total: totalSteps,
          succeeded: succeededCount,
          failed: failedCount,
          message
        });
        
        // Finish the progress dialog
        this.progressService.finishProgress(
          progressDialog, 
          failedSteps.length === 0
        );
        
        // Signal that publishing flow is complete (except cancellation)
        this._publishComplete.next(!wasCanceled);
        this.updatePendingChangesCount(events);
        
        // If loadTasks function was provided, call it to refresh the data
        if (loadTasks) {
          // Get the current date from the first event
          const currentDate = events.length > 0 
            ? format(new Date(events[0].start_date), 'yyyy-MM-dd')
            : format(new Date(), 'yyyy-MM-dd');
          
          // Reload tasks with the current date and restore scroll position
          loadTasks(currentDate, true);
        }
      })
    ).subscribe({
      error: (error: unknown) => {
        // Since API always returns 200, this handler is only for network/system failures
        const errorMessage = error instanceof Error ? error.message : String(error);
        progressDialog.setError(`A network error occurred: ${errorMessage}`);
        this.notificationService.addMessage('critical', 'A network error occurred while publishing changes: ' + errorMessage);
      }
    });
  }
}
